/*
  В качестве исключения решили выключить правила про 200 строк в этом файле.
  Хук самостоятельный нет особо мест для разбиения
*/
/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { main } from '@popperjs/core';
import { callChildAsFunction, dimension, isBrowser, popperModifiers, useStyleProps } from '../utils';
import { useKeyboardNavigation, useUniqID } from '../hooks';
import { useResizeObserver } from '../_internal/hooks';
export const useMenu = ({
  button,
  placement = 'bottom',
  position = 'absolute',
  fallbackPlacement,
  inheritWidth = false,
  children,
  offset,
  open = null,
  closeFunction = null,
  onSelect,
  selectedValues,
  closeOnSelect,
  untied,
  coords,
  topOffset,
  ...props
}) => {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [itemIndex, setItemIndex] = useState(0);
  const openState = open != null ? open : isOpen;
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const buttonRefElement = buttonRef.current;
  let buttonWidthRef = useRef(null);
  const menuId = useUniqID(5, 'menu-id');
  // eslint ругается на isOpen, который тут нужен.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const domMenu = useMemo(() => isBrowser() ? document.querySelector(`[data-menuid = ${menuId}]`) : null, [menuId, isOpen]);
  // eslint ругается на openState, который тут нужен.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const domItems = useMemo(() => domMenu == null ? void 0 : domMenu.querySelectorAll('[data-menu-item], [data-dom-listitem-value]'), [openState, domMenu]);
  const itemsList = useMemo(() => domItems != null && domItems.length ? Array.from(domItems).filter(item => item.getAttribute('aria-disabled') !== 'true') : null, [domItems]);
  const topOffsetModifier = React.useMemo(() => ({
    name: 'topOffset',
    enabled: true,
    phase: main,
    fn: ({
      state
    }) => {
      if (state.placement === 'top' && !!topOffset) {
        const newTopOffset = state.modifiersData.popperOffsets.y - topOffset;
        state.modifiersData.popperOffsets.y = newTopOffset;
      }
      return state;
    }
  }), [topOffset]);
  const modifiers = popperModifiers({
    fallback: fallbackPlacement,
    offset,
    listeners: openState
  });
  if (topOffset && topOffsetModifier) {
    modifiers.push(topOffsetModifier);
  }
  const {
    styles: popperStyles,
    attributes
  } = usePopper(buttonRefElement, listRef.current, {
    placement,
    strategy: position,
    modifiers
  });
  useResizeObserver(buttonRef, rect => {
    buttonWidthRef.current = rect.width;
  });
  // eslint ругается на buttonWidthRef.current, который тут нужен.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const width = useMemo(() => inheritWidth && buttonWidthRef.current ? buttonWidthRef.current : null, [buttonWidthRef.current, inheritWidth]);
  const popoverStyles = useMemo(() => untied ? {
    position,
    inset: coords == null ? void 0 : coords.join(' ')
  } : popperStyles.popper, [coords, position, untied, popperStyles.popper]);
  const {
    style
  } = useStyleProps({
    width
  }, menuStyleProps, popoverStyles);
  const mount = () => setMounted(true);
  const {
    onKeyDown: onArrowPress,
    onMouseEnter,
    selectedItem,
    selectItem,
    reset,
    index
  } = useKeyboardNavigation(itemsList);
  const close = useCallback(() => {
    closeFunction == null ? void 0 : closeFunction();
    setIsOpen(false);
    setItemIndex(index);
    reset();
  }, [closeFunction, index, reset]);
  useEffect(() => {
    if (openState && !mounted) {
      mount();
    }
  }, [openState, mounted]);
  useEffect(() => {
    if (React.Children.count(children) && Object.keys(attributes).length && openState && index === -1) {
      selectItem(children != null && children[itemIndex] ? itemIndex : 0);
    }
  }, [attributes, selectItem, openState, index, itemIndex, children]);
  useEffect(() => selectedItem == null ? void 0 : selectedItem.focus(), [selectedItem]);
  const onMouseOver = e => {
    const element = e.target;
    if (element.hasAttribute('data-dom-listitem-value') || element.hasAttribute('data-menu-item')) onMouseEnter(itemsList == null ? void 0 : itemsList.findIndex(item => item === element));
  };
  const selectAndClose = value => {
    const nextSelected = Array.isArray(selectedValues) && value ? [...selectedValues, value] : value;
    onSelect == null ? void 0 : onSelect(nextSelected);
    close();
  };
  const onKeyDown = event => {
    if (openState) {
      if (event.key === 'Tab') {
        event.preventDefault();
        close();
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        onArrowPress(event);
      }
      if (selectedItem && event.key === 'Enter') {
        event.preventDefault();
        const value = selectedItem.dataset.domListitemValue || null;
        const valueType = selectedItem.dataset.domListitemType;
        selectAndClose(valueType === 'number' ? Number(value) : value);
      }
    }
  };
  const buttonAction = useCallback(() => {
    if (!mounted) mount();else if (openState) close();else setIsOpen(true);
  }, [mounted, openState, close]);
  const providerValue = useMemo(() => ({
    buttonRef,
    action: buttonAction,
    isMenuOpen: openState
  }), [buttonRef, openState, buttonAction]);
  return {
    focusOnAttributes: {
      enabled: mounted && openState,
      autoFocus: false,
      onClickOutside: close,
      onEscapeKey: close
    },
    menuContextAttributes: {
      value: providerValue,
      children: button
    },
    portalAttributes: {
      onMount: () => setIsOpen(!!(listRef.current && mounted))
    },
    popoverAttributes: {
      style,
      onKeyDown,
      onMouseOver,
      ref: listRef,
      'data-menuid': menuId,
      'data-open': openState,
      ...attributes.popper
    },
    listAttributes: {
      onSelect: closeOnSelect ? selectAndClose : onSelect,
      selectedValues,
      children: callChildAsFunction(children),
      ...props
    },
    open: openState,
    mounted
  };
};
const menuStyleProps = {
  width: ['width', dimension]
};